import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserRoundIcon } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { useProfile } from '@/data/useProfile';

/**
 * Above every screen while the caller has no profile row (design 5.3): "Add your name so your team sees
 * who did what", a link to My profile, and Dismiss, remembered per user in this browser. Storage may be
 * blocked (a private window): the banner then simply shows again next time.
 */

const keyOf = (userId: string) => `coreway.profileBanner.${userId}`;

function readDismissed(userId: string): boolean {
  try {
    return localStorage.getItem(keyOf(userId)) === 'dismissed';
  } catch {
    return false;
  }
}

function writeDismissed(userId: string): void {
  try {
    localStorage.setItem(keyOf(userId), 'dismissed');
  } catch {
    // Blocked storage: dismissed for this page view only.
  }
}

export function ProfileBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { status, profile } = useProfile();
  const userId = user?.id ?? null;
  const [dismissed, setDismissed] = React.useState(() => (userId ? readDismissed(userId) : false));

  React.useEffect(() => {
    if (userId) setDismissed(readDismissed(userId));
  }, [userId]);

  if (!userId || status !== 'ready' || profile !== null || dismissed) return null;
  // On My profile itself the form is the call to action: the banner would link to the page it sits on.
  if (pathname.startsWith('/settings/profile')) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-hairline bg-card px-4 py-3 shadow-card">
      <UserRoundIcon className="size-5 shrink-0 text-accent-base" aria-hidden />
      <p className="min-w-0 flex-1 text-base text-ink">{t('profile.banner.text')}</p>
      <div className="flex items-center gap-2">
        <Button asChild size="sm" variant="outline">
          <Link to="/settings/profile">{t('profile.banner.action')}</Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            writeDismissed(userId);
            setDismissed(true);
          }}
        >
          {t('profile.banner.dismiss')}
        </Button>
      </div>
    </div>
  );
}
